/*
 * A business's palette, applied to Ionic.
 *
 * A tenant stores one colour — `{ accent: '#0369a1' }` — and everything else is
 * derived here. That is the same decision apps/web/src/lib/theme.ts makes and
 * for the same reason: the accent is large and text-bearing, so the ink that
 * sits ON it has to be paired with it rather than left to whatever the
 * stylesheet's colour scheme last chose. A dark accent with the light theme's
 * near-black contrast is about 1.6:1, which is not a style bug.
 *
 * What is different here is the target. Ionic components read
 * `--ion-color-primary` and its five companions, not our own custom properties,
 * so a tenant accent that is not written into those is a themed header sitting
 * above unthemed buttons.
 */

import type { TenantChrome } from '@blooq/flow'

/** Accepts `#rgb` and `#rrggbb`. Anything else is null rather than a guess. */
export function parseHex(value: string): [number, number, number] | null {
  const hex = value.trim().replace(/^#/, '')

  if (hex.length === 3) {
    const parts = [...hex].map((c) => parseInt(c + c, 16))
    if (parts.some(Number.isNaN)) return null
    return [parts[0]!, parts[1]!, parts[2]!]
  }

  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    if (Number.isNaN(r + g + b)) return null
    return [r, g, b]
  }

  return null
}

/** sRGB relative luminance, per WCAG 2.x. */
function luminance([r, g, b]: [number, number, number]): number {
  const channel = (value: number) => {
    const v = value / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/**
 * Black or white, whichever contrasts better.
 *
 * The 0.36 threshold is deliberately above the mathematical crossover, and it
 * is copied from the web app rather than re-derived: the two surfaces render
 * the same business, and a header that is white-on-accent in one and
 * black-on-accent in the other is the sort of difference nobody can explain
 * afterwards.
 */
export function readableInk(background: string): string | null {
  const rgb = parseHex(background)
  if (!rgb) return null
  return luminance(rgb) > 0.36 ? '#131513' : '#ffffff'
}

function mix(rgb: [number, number, number], towards: number, amount: number): string {
  const channel = (value: number) => Math.round(value + (towards - value) * amount)
  return `#${rgb
    .map(channel)
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')}`
}

function setVar(name: string, value: string) {
  document.documentElement.style.setProperty(name, value)
}

/**
 * Point every Ionic surface at this business's colour.
 *
 * `shade` and `tint` are what Ionic uses for pressed and hovered states; left
 * unset they stay at the default blue, so a tenant with a green accent gets a
 * green button that flashes blue under a finger.
 */
function applyAccent(accent: string, explicitInk?: string) {
  const rgb = parseHex(accent)
  if (!rgb) return

  const ink = explicitInk ?? readableInk(accent) ?? '#ffffff'
  const inkRgb = parseHex(ink) ?? [255, 255, 255]

  setVar('--ion-color-primary', accent)
  setVar('--ion-color-primary-rgb', rgb.join(', '))
  setVar('--ion-color-primary-contrast', ink)
  setVar('--ion-color-primary-contrast-rgb', inkRgb.join(', '))
  setVar('--ion-color-primary-shade', mix(rgb, 0, 0.12))
  setVar('--ion-color-primary-tint', mix(rgb, 255, 0.1))

  // Our own tokens, for the parts of the chat that are not Ionic components.
  setVar('--blooq-accent', accent)
  setVar('--blooq-accent-ink', ink)

  /*
   * The browser chrome: the address bar on Android, and the strip behind the
   * status bar in an installed PWA. Left at the fallback it is the one piece of
   * the app that stays somebody else's colour.
   */
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', accent)
}

/**
 * The single letter used for a business's app icon.
 *
 * Only A-Z and 0-9, and the restriction is the point: a Devanagari business
 * name asked for its first character draws tofu on a customer's home screen,
 * which is worse than a plain accent tile. The first Latin character anywhere
 * in the name wins, so "Shree Salon" written in Devanagari still yields "S".
 * A name with none gives an empty string and a plain accent tile — still a
 * valid icon, and still enough to make the app installable, which is the one
 * thing that must not fail.
 */
export function monogram(name: string): string {
  const match = name.toUpperCase().match(/[A-Z0-9]/)
  return match ? match[0] : ''
}

/**
 * The app icon, drawn here rather than fetched.
 *
 * Sign-up never asks for a logo — six fields is the whole form — so most
 * businesses have none, and Chrome will not offer to install a manifest with no
 * icon of at least 192px. The web app solves this with a server-rendered
 * monogram route; a static bundle has no server, so it draws the same thing on
 * a canvas.
 */
function drawIcon(chrome: TenantChrome, size: number): string | null {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    const accent = chrome.theme?.accent ?? '#0369a1'
    ctx.fillStyle = parseHex(accent) ? accent : '#0369a1'
    ctx.fillRect(0, 0, size, size)

    const letter = monogram(chrome.businessName)
    if (letter) {
      ctx.fillStyle = readableInk(accent) ?? '#ffffff'
      ctx.font = `600 ${Math.round(size * 0.52)}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      // Optical centre, not geometric: cap height sits above the em box's
      // middle, so a letter centred by the numbers reads low.
      ctx.fillText(letter, size / 2, size * 0.54)
    }

    return canvas.toDataURL('image/png')
  } catch {
    // A blocked canvas (some privacy modes fingerprint-guard toDataURL) falls
    // back to the bundled icon. Installable either way, which is what matters.
    return null
  }
}

/**
 * A manifest for THIS business, replacing the unbranded one in the bundle.
 *
 * The bundled manifest names no business on purpose — one static file cannot
 * describe every tenant, and naming a business the build does not know would be
 * a lie. So it is swapped for a generated one as soon as the server says who
 * this is.
 *
 * Every url in it is absolute. A blob url has no meaningful base to resolve a
 * relative `start_url` against, and a manifest whose start_url resolves
 * somewhere unexpected produces an installed app that opens on a blank page.
 *
 * Best-effort by design. If a browser declines a generated manifest the bundled
 * one is still linked and the app is still installable — with generic chrome,
 * which is a worse first impression and not a broken app.
 */
function applyManifest(chrome: TenantChrome, icon: string) {
  try {
    const origin = window.location.origin
    const accent = chrome.theme?.accent ?? '#0369a1'
    const name = chrome.businessName || 'Blooq'

    const manifest = {
      name,
      short_name: name.slice(0, 18),
      description: chrome.tagline || `Book with ${name}.`,
      id: `${origin}/`,
      start_url: `${origin}/`,
      scope: `${origin}/`,
      display: 'standalone',
      orientation: 'portrait',
      theme_color: accent,
      background_color: accent,
      icons: [
        { src: icon, sizes: '192x192', type: 'image/png' },
        { src: icon, sizes: '512x512', type: 'image/png' },
        { src: icon, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
      ],
    }

    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' })
    const link = document.querySelector('link[rel="manifest"]')
    if (link) link.setAttribute('href', URL.createObjectURL(blob))
  } catch {
    // The bundled manifest stays linked. See above.
  }
}

/**
 * Name and icon, in the places each platform actually reads.
 *
 * iOS ignores the manifest entirely and reads `apple-touch-icon`, which is why
 * that is set separately rather than assumed to follow. It is also snapshotted
 * at install and never updated — a business that rebrands reaches an
 * already-installed iOS home screen only by being removed and re-added, and no
 * amount of care here changes that.
 */
function applyIdentity(chrome: TenantChrome) {
  document.title = chrome.businessName
    ? `${chrome.businessName} · Book an appointment`
    : 'Book an appointment'

  const icon = chrome.logoUrl ?? drawIcon(chrome, 512)
  if (!icon) return

  const apple = document.querySelector('link[rel="apple-touch-icon"]')
  if (apple) apple.setAttribute('href', icon)

  applyManifest(chrome, icon)
}

/** Everything a business's identity touches, in one call. */
export function applyTenantTheme(chrome: TenantChrome) {
  const theme = chrome.theme ?? {}
  if (theme.accent) applyAccent(theme.accent, theme.accentInk ?? theme['accent-ink'])

  // Anything else the tenant stored — a business that has been given a fuller
  // palette gets it, without this file needing to know the key names.
  for (const [key, value] of Object.entries(theme)) {
    if (key !== 'accent') setVar(`--blooq-${key}`, value)
  }

  applyIdentity(chrome)
}
